import { useContext, useEffect, useState, useMemo } from 'react'
import { t } from '@lingui/core/macro'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import { arrayOf, bool, number, oneOf, oneOfType, shape, string } from 'prop-types'
import { CvsAutoComplete, CvsTextField, CvFlexBox } from '@components'
import { connectorContext } from '@contexts'
import { ClassicConfig, TransparentConfig } from './components'
import {
  qrCodeCustomizationOptions,
  configOptions,
  fileTypes,
  fileTypeSelectOptions,
  qrCodeTypes,
  integrationName,
} from './config'
import { useStyles } from './styles'
import { getNameAndAccountErrors, getMetadataErrors, isValidJson } from './util'
import { EditTemplateContext } from '../../../edit-template-context'
import { templateCopyContext } from '../../context'

export const QrCodeMonkey = props => {
  const { assignment } = props
  const {
    name: assignmentName,
    connector_id,
    metadata: {
      qr_code_monkey_type,
      qr_code_monkey_use_shortened,
      qr_code_monkey_dont_regenerate,
      qr_code_monkey_size,
      qr_code_monkey_file_type,
      qr_code_monkey_config,
      qr_code_monkey_background,
      qr_code_monkey_x_offset,
      qr_code_monkey_y_offset,
    },
  } = assignment
  const classes = useStyles()
  const { onChange } = useContext(templateCopyContext)
  const { configNotifications, setConfigNotifications } = useContext(EditTemplateContext)
  const { byId } = useContext(connectorContext)
  const availableConnectors = useMemo(() => {
    const allConnectors = Object.values(byId)
    const qrCodeMonkeyConnector = allConnectors.filter(
      ({ connector_type_id }) =>
        connector_type_id ===
        (assignment.connector_type_id || byId[assignment.connector_id].connector_type_id),
    )
    const convertedToValidSelectOption = qrCodeMonkeyConnector.map(c => ({
      label: c.name,
      value: c.id,
    }))

    return convertedToValidSelectOption
  }, [assignment, byId])

  const [showConfigOptions, setShowConfigOptions] = useState(false)
  const [validatingJson, setValidatingJson] = useState(false)
  const [jsonValidated, setJsonValidated] = useState(true)
  const [jsonData, setJsonData] = useState({})
  const [requiredInputs, setRequiredInputs] = useState({
    name: assignmentName || '',
    connector_id: connector_id || '',
    metadata: {
      qr_code_monkey_type: qr_code_monkey_type || 'classic',
      qr_code_monkey_use_shortened: qr_code_monkey_use_shortened || '0',
      qr_code_monkey_dont_regenerate: qr_code_monkey_dont_regenerate || '0',
      qr_code_monkey_size: parseInt(qr_code_monkey_size, 10) || 300,
      qr_code_monkey_file_type: qr_code_monkey_file_type || 'svg',
      qr_code_monkey_config: qr_code_monkey_config || '{}',
      qr_code_monkey_background: qr_code_monkey_background || '',
      qr_code_monkey_x_offset: qr_code_monkey_x_offset || '',
      qr_code_monkey_y_offset: qr_code_monkey_y_offset || '',
    },
  })

  const {
    qrCodeType,
    qrCodeUseShortened,
    qrCodeDontRegenerate,
    qrCodeSize,
    qrCodeFileType,
    qrCodeConfig,
  } = qrCodeCustomizationOptions

  useEffect(() => {
    updateAssignment()
    handleValidateConfigObject()
  }, [])

  useEffect(() => {
    updateErrors()
  }, [assignment, jsonValidated])

  const updateAssignment = () => {
    for (const key in requiredInputs) {
      handleChange(key, requiredInputs[key])
    }
  }

  const updateErrors = (inputs = requiredInputs) => {
    const nameAndAccountErrors = getNameAndAccountErrors(inputs)
    const metadataErrors = getMetadataErrors(inputs, jsonValidated)

    setConfigNotifications(prevState => {
      const prevErrors = prevState.errors.filter(e => e.reference.step !== 'connect')
      return {
        ...prevState,
        errors: [...prevErrors, ...nameAndAccountErrors, ...metadataErrors],
      }
    })
  }

  const handleMetadataChange = (key, value) => {
    let updatedValue = value

    if (key === qrCodeConfig) {
      updatedValue = value === '' ? '{}' : value
      setJsonValidated(false)
    }

    if (key === qrCodeUseShortened) {
      updatedValue = value === true ? '1' : '0'
    }

    if (key === qrCodeDontRegenerate) {
      updatedValue = value === true ? '1' : '0'
    }

    onChange(prev => ({
      ...prev,
      [assignment.id]: {
        ...prev[assignment.id],
        metadata: {
          ...prev[assignment.id].metadata,
          [key]: updatedValue,
        },
      },
    }))
    return {
      ...requiredInputs,
      metadata: {
        ...requiredInputs.metadata,
        [key]: updatedValue,
      },
    }
  }

  const handleChange = (key, value) => {
    const updatedInputs = (() => {
      if (key.includes('qr_code')) {
        return handleMetadataChange(key, value)
      }
      onChange(prev => ({
        ...prev,
        [assignment.id]: {
          ...prev[assignment.id],
          [key]: value,
        },
      }))
      return {
        ...requiredInputs,
        [key]: value,
      }
    })()
    setRequiredInputs(updatedInputs)
    updateErrors(updatedInputs)
  }

  const getErrorMsg = idName => {
    if (!configNotifications.errors.length) return ''
    return configNotifications.errors.filter(e => e?.reference?.id === idName)[0]?.message
  }

  const validateKeys = obj => {
    Object.keys(obj).forEach(key => {
      if (!configOptions[key]) {
        setJsonValidated(false)
        setShowConfigOptions(true)
        setConfigNotifications(prevState => ({
          ...prevState,
          errors: [
            ...prevState.errors,
            {
              type: 'ERROR',
              message: t`"${key}" is not a valid property for ${integrationName} Configuration`,
              reference: {
                step: 'connect',
                id: qrCodeConfig,
              },
            },
          ],
        }))
      } else {
        setShowConfigOptions(false)
      }
    })
  }

  const updateConfigErrors = e => {
    setConfigNotifications(prevState => ({
      ...prevState,
      errors: [
        ...prevState.errors,
        {
          type: 'ERROR',
          message: t`Invalid JSON: ${e}`,
          reference: {
            step: 'connect',
            id: qrCodeConfig,
          },
        },
      ],
    }))
  }

  const handleValidateConfigObject = () => {
    const config = requiredInputs.metadata.qr_code_monkey_config
    setValidatingJson(true)
    setTimeout(() => {
      if (isValidJson(config, updateConfigErrors)) {
        setJsonValidated(true)
        const configJson = JSON.parse(config)
        validateKeys(configJson)
        setJsonData(configJson)
      }
      setValidatingJson(false)
    }, 250)
  }

  return (
    <Box width="100%">
      <CvsTextField
        error={getErrorMsg('name')}
        id="name"
        label={t`Name`}
        onChange={e => handleChange('name', e.target.value)}
        required
        style={{ width: '50%' }}
        value={requiredInputs.name}
      />
      <CvsAutoComplete
        id="connector_id"
        label={t`Account`}
        onSelected={selected => handleChange('connector_id', selected)}
        options={availableConnectors}
        required
        selected={requiredInputs.connector_id}
        style={{ width: '50%' }}
      />
      <Typography variant="h4" className={classes.subHeading}>
        {t`QR code customization`}
      </Typography>
      <CvFlexBox justifyContent="start" alignItems="start">
        <CvsTextField
          error={getErrorMsg(qrCodeSize)}
          id={qrCodeSize}
          label={t`Size (px)`}
          onChange={e => handleChange(qrCodeSize, parseInt(e.target.value, 10))}
          required
          style={{ width: '50%' }}
          type="number"
          value={requiredInputs.metadata.qr_code_monkey_size}
        />
        <Box ml={2} width="50%">
          <CvsAutoComplete
            defaultValue={requiredInputs.metadata.qr_code_monkey_file_type}
            id={qrCodeFileType}
            label={t`File type`}
            noMargin
            onSelected={selected => handleChange(qrCodeFileType, selected)}
            options={fileTypeSelectOptions}
            required
            selected={requiredInputs.metadata.qr_code_monkey_file_type}
          />
        </Box>
      </CvFlexBox>
      <Box>
        <RadioGroup
          id={qrCodeType}
          onChange={(e, value) => handleChange(qrCodeType, value)}
          value={requiredInputs.metadata.qr_code_monkey_type}
        >
          <FormControlLabel
            value={qrCodeTypes.classic.value}
            label={qrCodeTypes.classic.label}
            control={<Radio />}
          />
          <FormControlLabel
            value={qrCodeTypes.transparent.value}
            label={qrCodeTypes.transparent.label}
            control={<Radio />}
          />
        </RadioGroup>
        <>
          <FormControlLabel
            label={t`Use shortened link if available`}
            control={
              <Checkbox
                checked={requiredInputs.metadata.qr_code_monkey_use_shortened === '1'}
                color="secondary"
                id={qrCodeUseShortened}
                onChange={e => handleChange(qrCodeUseShortened, e.target.checked)}
              />
            }
          />
        </>
        <>
          <FormControlLabel
            label={t`Don't regenerate QR code if already exists`}
            control={
              <Checkbox
                checked={requiredInputs.metadata.qr_code_monkey_dont_regenerate === '1'}
                color="secondary"
                id={qrCodeDontRegenerate}
                onChange={e => handleChange(qrCodeDontRegenerate, e.target.checked)}
              />
            }
          />
        </>
      </Box>
      {requiredInputs.metadata.qr_code_monkey_type === 'classic' ? (
        <ClassicConfig
          errors={[getErrorMsg(qrCodeConfig), getErrorMsg('jsonValidated')]}
          handleChange={handleChange}
          handleValidateConfigObject={handleValidateConfigObject}
          jsonData={jsonData}
          jsonValidated={jsonValidated}
          showConfigOptions={showConfigOptions}
          validatingJson={validatingJson}
          value={requiredInputs.metadata.qr_code_monkey_config}
        />
      ) : (
        <TransparentConfig
          handleChange={handleChange}
          background={requiredInputs.metadata.qr_code_monkey_background}
          xOffset={requiredInputs.metadata.qr_code_monkey_x_offset}
          yOffset={requiredInputs.metadata.qr_code_monkey_y_offset}
        />
      )}
    </Box>
  )
}

QrCodeMonkey.propTypes = {
  assignment: shape({
    name: string,
    assigned_entity_type: string,
    id: number,
    integration_id: number,
    slug: string,
    integration_type: string,
    type: string,
    filename_pattern: string,
    folder: string || null,
    report_type: string,
    include_summary_header: bool,
    included_output_fields: arrayOf(
      shape({
        display: string,
        id: number,
        isHidden: bool,
        isMeta: bool,
        name: string,
        selected: bool,
      }),
    ),
    file_type: arrayOf(oneOf(fileTypes)),
    url: string,
    assigned_integration_id: number,
    connector_id: number || null,
    connector_type_id: number,
    metadata: shape({
      qr_code_monkey_config: string,
      qr_code_monkey_file_type: oneOf(fileTypes),
      qr_code_monkey_size: oneOfType([number, string]),
      qr_code_monkey_type: oneOf(Object.keys(qrCodeTypes)),
      qr_code_monkey_use_shortened: oneOf(['0', '1']),
      qr_code_monkey_dont_regenerate: oneOf(['0', '1']),
      qr_code_monkey_background: string,
      qr_code_monkey_x_offset: oneOfType([number, string]),
      qr_code_monkey_y_offset: oneOfType([number, string]),
    }),
    config_hash: string || number,
  }).isRequired,
}
